import { useState, useEffect, useRef } from "react";
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { Box, Typography, colors } from '@mui/material';
import { Button, Select, MenuItem, FormControl, InputLabel, Chip, OutlinedInput, CircularProgress } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import axios from "axios";
import { toast } from 'react-toastify';

import "./HubspotModal.css";

import { API_URL } from "../../../../utils/auth";

const ONBOARDING_STEPS = [
  { slug: 'contract_signed', label: 'Contract Signed', field: 'contractSigned' },
  { slug: 'welcome_call', label: 'Welcome Call', field: 'welcomeCall' },
  { slug: 'onboarding_survey', label: 'Onboarding Survey', field: 'onboardingSurvey' },
  { slug: 'onboarding_call', label: 'Onboarding Call', field: 'onboardingCall' },
  { slug: 'group_access', label: 'Group Access', field: 'groupAccess' },
];

const HubspotModal = ({ onClose }) => {

  const [manageLoading, setManageLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [hubspotData, setHubspotData] = useState(null);
  const [syncStats, setSyncStats] = useState({ totalSyncedContacts: 0, lastSyncDate: null });
  const [syncStatsLoading, setSyncStatsLoading] = useState(false);
  const syncStatsRequestRef = useRef(null);
  const statusRequestRef = useRef(null);

  const [pipelines, setPipelines] = useState([]);
  const [pipelinesLoading, setPipelinesLoading] = useState(false);
  const [enabledSteps, setEnabledSteps] = useState([]);

  const DEFAULT_CONFIG = {
    contactSync: false,
    contactsSynced: 0,
    companySync: false,
    engagementSync: false,
    frequency: 'realtime',
    contactFilter: { pipelineId: '', stageIds: [] },
    stageOnboardingMap: {}
  };

  const [hubspotConfig, setHubspotConfig] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    loadHubspotSettings();
    loadSyncStats();
    loadPipelines();
    loadOnboardingSettings();

    const handleRefreshStats = () => {
      loadSyncStats();
    };

    window.addEventListener('refreshHubspotStats', handleRefreshStats);

    return () => {
      if (statusRequestRef.current) {
        statusRequestRef.current = null;
      }
      if (syncStatsRequestRef.current) {
        syncStatsRequestRef.current = null;
      }
      window.removeEventListener('refreshHubspotStats', handleRefreshStats);
    };
  }, []);

  const closeDialog = () => {
    setHubspotData(null);
    setHubspotConfig(null);
    setConfigLoaded(false);
    onClose();
  };

  const onManualSync = () => {
    onClose("manualSync");
  };

  const isEmpty = (obj) => {
    if (obj == null) return true;
    if (typeof obj === 'string' && obj.trim() === '') return true;
    if (typeof obj === 'string') return Object.keys(JSON.parse(obj)).length === 0;
    if (typeof obj !== 'object') return true;
    return Object.keys(obj).length === 0;
  };

  const loadPipelines = async () => {
    setPipelinesLoading(true);
    try {
      const response = await axios.get(`${API_URL}/hubspot/pipelines`);
      setPipelines(response.data.pipelines || []);
    } catch (err) {
      console.error("Failed to load pipelines:", err.message);
      setPipelines([]);
    } finally {
      setPipelinesLoading(false);
    }
  };

  const loadOnboardingSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/onboarding-settings`);
      const settings = response.data || [];
      const enabled = settings
        .filter(s => s.enabled)
        .map(s => s.slug);
      setEnabledSteps(enabled.length > 0 ? enabled : ONBOARDING_STEPS.map(s => s.slug));
    } catch (err) {
      console.error("Failed to load onboarding settings:", err.message);
      setEnabledSteps(ONBOARDING_STEPS.map(s => s.slug));
    }
  };

  const loadHubspotSettings = async () => {
    if (statusRequestRef.current) {
      return;
    }

    setManageLoading(true);
    const request = axios
      .get(`${API_URL}/hubspot/status`)
      .then((response) => {
        const data = {
          portalId: response?.data?.data?.team_id,
          portalName: response?.data?.data?.team_name,
        };

        setHubspotData(data);

        const rawConfig = response?.data?.data?.config;
        let parsed = null;
        try {
          parsed = rawConfig ? (typeof rawConfig === 'string' ? JSON.parse(rawConfig) : rawConfig) : null;
        } catch (e) {
          parsed = null;
        }
        const finalConfig = !parsed || isEmpty(parsed) ? DEFAULT_CONFIG : { ...DEFAULT_CONFIG, ...parsed };
        finalConfig.frequency = 'realtime';
        if (!finalConfig.contactFilter) {
          finalConfig.contactFilter = { pipelineId: '', stageIds: [] };
        }
        if (!finalConfig.stageOnboardingMap) {
          finalConfig.stageOnboardingMap = {};
        }
        setHubspotConfig(finalConfig);
        setConfigLoaded(true);
      })
      .catch((err) => {
        toast.error(`Failed to fetch HubSpot integration info: ${err.message}`);
        setHubspotConfig(DEFAULT_CONFIG);
        setConfigLoaded(true);
      })
      .finally(() => {
        setManageLoading(false);
        statusRequestRef.current = null;
      });

    statusRequestRef.current = request;
  };

  const loadSyncStats = () => {
    if (syncStatsRequestRef.current) {
      return;
    }

    setSyncStatsLoading(true);

    const request = axios
      .get(`${API_URL}/hubspot/sync-stats`)
      .then((response) => {
        setSyncStats(response.data);
      })
      .catch((err) => {
        console.error("Failed to load sync stats:", err.message);
        setSyncStats({ totalSyncedContacts: 0, lastSyncDate: null });
      })
      .finally(() => {
        setSyncStatsLoading(false);
        syncStatsRequestRef.current = null;
      });

    syncStatsRequestRef.current = request;
  };

  const handleToggle = (key) => setHubspotConfig(prev => ({ ...prev, [key]: !prev[key] }));

  const handlePipelineChange = (e) => {
    const pipelineId = e.target.value;
    setHubspotConfig(prev => ({
      ...prev,
      contactFilter: { pipelineId, stageIds: [] },
      stageOnboardingMap: {}
    }));
  };

  const handleStageChange = (e) => {
    const stageIds = e.target.value;
    setHubspotConfig(prev => ({
      ...prev,
      contactFilter: { ...prev.contactFilter, stageIds }
    }));
  };

  const handleStageOnboardingMap = (stepSlug, stageId) => {
    setHubspotConfig(prev => {
      const newMap = { ...prev.stageOnboardingMap };
      if (stageId === '') {
        delete newMap[stepSlug];
      } else {
        newMap[stepSlug] = stageId;
      }
      return { ...prev, stageOnboardingMap: newMap };
    });
  };

  const selectedPipeline = pipelines.find(p => p.id === hubspotConfig?.contactFilter?.pipelineId);
  const availableStages = selectedPipeline?.stages || [];
  const visibleOnboardingSteps = ONBOARDING_STEPS.filter(s => enabledSteps.includes(s.slug));

  const updateHubspotConfig = () => {
    if (!hubspotConfig?.contactFilter?.pipelineId) {
      toast.error('Please select a deal pipeline before saving.');
      return;
    }
    if (!hubspotConfig?.contactFilter?.stageIds?.length) {
      toast.error('Please select at least one deal stage before saving.');
      return;
    }

    setUpdateLoading(true);

    const configToSave = {
      ...hubspotConfig,
      frequency: 'realtime'
    };

    axios
      .post(`${API_URL}/hubspot/config`, configToSave)
      .then(() => {
        toast.success('HubSpot settings have been updated successfully!');
        closeDialog();
      })
      .catch((err) => {
        console.error("Failed to update HubSpot settings:", err.message);
        toast.error('Failed to update HubSpot settings!');
      })
      .finally(() => setUpdateLoading(false));
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog">
        <h2>Manage HubSpot Settings</h2>
        {hubspotData ? (
          <>
            <div className="hubspot-branding">
              <img
                src="/hubspot.png"
                alt="HubSpot"
                className="hubspot-portal-icon"
              />
              <div className="hubspot-portal-meta">
                <h3>HubSpot Portal</h3>
                <p>Portal ID: {hubspotData.portalId || 'N/A'}</p>
              </div>
            </div>
            <div className="hubspot-main-layout">
              <div className="hubspot-left-panel">
                <div className="hubspot-sync-stats">
                  {syncStatsLoading ? (
                    <CircularProgress size={24} sx={{ color: '#805ad5' }} />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: '48px',
                        fontWeight: 'bold',
                        color: '#805ad5',
                        lineHeight: 1,
                        mb: 1
                      }}
                    >
                      {syncStats.totalSyncedContacts || 0}
                    </Typography>
                  )}
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#333',
                      textAlign: 'center',
                      mb: 2,
                      lineHeight: 1.4
                    }}
                  >
                    Number of Actively Synced<br />Contacts in ClientBloom
                  </Typography>
                  <Button
                    className="sync-button"
                    sx={{
                      backgroundColor: '#805ad5',
                      color: 'white',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#6b46c1'
                      }
                    }}
                    onClick={onManualSync}
                    variant="contained"
                    size="large"
                    startIcon={<PlayArrowRoundedIcon sx={{ color: 'white' }} />}
                    disabled={updateLoading}
                  >
                    Click to Manually Sync
                  </Button>
                </div>
              </div>
              <div className="hubspot-right-panel">
                <div className="notification-section">
                  <div className="section-control">
                    <h3>Client Source — Deal Pipeline & Stage</h3>
                  </div>
                  <p className="toggle-description-i" style={{ marginBottom: '12px' }}>
                    Select which deal pipeline and stage(s) represent your active clients. Only contacts associated with deals in the selected stage(s) will be synced.
                  </p>
                  {pipelinesLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                      <CircularProgress size={20} />
                    </div>
                  ) : (
                    <>
                      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Deal Pipeline</InputLabel>
                        <Select
                          value={hubspotConfig?.contactFilter?.pipelineId || ''}
                          onChange={handlePipelineChange}
                          label="Deal Pipeline"
                        >
                          {pipelines.map((p) => (
                            <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {hubspotConfig?.contactFilter?.pipelineId && (
                        <FormControl fullWidth size="small">
                          <InputLabel>Deal Stage(s)</InputLabel>
                          <Select
                            multiple
                            value={hubspotConfig?.contactFilter?.stageIds || []}
                            onChange={handleStageChange}
                            input={<OutlinedInput label="Deal Stage(s)" />}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((id) => {
                                  const stage = availableStages.find(s => s.id === id);
                                  return <Chip key={id} label={stage?.label || id} size="small" />;
                                })}
                              </Box>
                            )}
                          >
                            {availableStages.map((s) => (
                              <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </>
                  )}
                </div>

                {hubspotConfig?.contactFilter?.pipelineId && availableStages.length > 0 && (
                  <div className="notification-section">
                    <div className="section-control">
                      <h3>Onboarding Stage Mapping</h3>
                    </div>
                    <p className="toggle-description-i" style={{ marginBottom: '12px' }}>
                      Map each onboarding step to a HubSpot deal stage. When a deal reaches or passes a mapped stage, that step is automatically checked off. Steps stay checked even when the deal moves forward.
                    </p>
                    <div className="stage-mapping-list">
                      {visibleOnboardingSteps.map((step) => (
                        <div key={step.slug} className="stage-mapping-row">
                          <Typography
                            sx={{
                              fontSize: '13px',
                              fontWeight: 600,
                              color: '#333',
                              minWidth: '140px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            {step.label}
                          </Typography>
                          <ArrowForwardIcon sx={{ color: '#999', fontSize: '18px', mx: 1 }} />
                          <FormControl size="small" sx={{ flex: 1 }}>
                            <Select
                              value={hubspotConfig?.stageOnboardingMap?.[step.slug] || ''}
                              onChange={(e) => handleStageOnboardingMap(step.slug, e.target.value)}
                              displayEmpty
                              sx={{ fontSize: '13px' }}
                            >
                              <MenuItem value="">
                                <em>Not mapped</em>
                              </MenuItem>
                              {availableStages.map((s) => (
                                <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="notification-section">
                  <div className="section-control">
                    <h3>Contact & Engagement</h3>
                  </div>
                  {!configLoaded || !hubspotConfig ? (
                    <div className="toggle-list" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                      <CircularProgress size={20} />
                    </div>
                  ) : (
                    <div className="toggle-list">
                      <div className="toggle-item">
                        <div className="toggle-header-left">
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={hubspotConfig.contactSync ?? false}
                              onChange={() => handleToggle("contactSync")}
                            />
                            <span className="slider"></span>
                          </label>
                          <span className="toggle-title">Two-Way Contact Sync</span>
                        </div>
                        <p className="toggle-description-i">Contacts sync automatically between HubSpot and ClientBloom</p>
                      </div>
                      <div className="toggle-item">
                        <div className="toggle-header-left">
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={hubspotConfig.companySync ?? false}
                              onChange={() => handleToggle("companySync")}
                            />
                            <span className="slider"></span>
                          </label>
                          <span className="toggle-title">Company Data Sync</span>
                        </div>
                        <p className="toggle-description-i">Keep company info, size, and industry aligned</p>
                      </div>
                      <div className="toggle-item">
                        <div className="toggle-header-left">
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={hubspotConfig?.engagementSync ?? false}
                              onChange={() => handleToggle("engagementSync")}
                            />
                            <span className="slider"></span>
                          </label>
                          <span className="toggle-title">Engagement Event Sync</span>
                        </div>
                        <p className="toggle-description-i">Sync calls, emails, and meetings from HubSpot on schedule</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="notification-section">
                  <div className="section-control">
                    <h3>Integration Info</h3>
                  </div>
                  <div className="channel-selection">
                    <p className="channel-description">
                      Connected to HubSpot Portal: <strong>{hubspotData.portalId || 'N/A'}</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", margin: "20px 0", height: '90px' }}>
            <CircularProgress
              size={36}
              sx={{
                color: colors.blue[500],
                marginRight: 4,
              }}
            />
            <p>Loading HubSpot integration info...</p>
          </div>
        )}
        <div className="dialog-actions">
          <Box
            sx={{ m: 1, position: 'relative' }}>
            <Button
              className="update-button"
              sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
              onClick={updateHubspotConfig}
              variant="outlined"
              color="success"
              size="large"
              disabled={updateLoading}
            >
              Update
            </Button>
            {updateLoading && (
              <CircularProgress
                size={24}
                sx={{
                  color: colors.green[500],
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
          <Box
            sx={{ m: 1, position: 'relative' }}>
            <Button
              className="close-button"
              sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
              onClick={closeDialog}
              variant="outlined"
              color="error"
              size="large"
            >
              Close
            </Button>
          </Box>
        </div>
      </div>
    </div>
  );
};

export default HubspotModal;
